import { useState, useEffect } from "react";
import { todayYmd, receiptDateToYmd } from "@shared/dates";
import { hasFullAccess } from "@shared/access";
import { useQuery, useMutation } from "@tanstack/react-query";
import { track } from "@/lib/analytics";
import { maybeAskForReview } from "@/lib/native-review";
import { apiRequest, apiFormRequest, queryClient } from "@/lib/queryClient";
import type { SafeUser, Expense } from "@shared/schema";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, Plus, Users2, HandCoins, CheckCircle2, ChevronRight, Camera, X, Repeat, Crown, Copy, Check, MessageCircle, Share2 } from "lucide-react";
import { shareAppLink } from "@/lib/share";
import { ShareChannels } from "@/components/ShareChannels";
import { Switch } from "@/components/ui/switch";
import { isInTWA } from "@/lib/platform";
import { ScanReceiptButton } from "@/components/ScanReceiptButton";
import { CurrencySelector, formatMoney, currencySymbol } from "@/components/CurrencySelector";
import { useToast } from "@/hooks/use-toast";
import { Link } from "wouter";
import { useAuth } from "@/lib/auth";
import { calculateGroupBalances, calculatePairwiseBalances } from "@/lib/simplify";
import { displayBalance, isEffectivelySettled, AMOUNT_IN_CLASS, AMOUNT_OUT_CLASS } from "@/lib/balance-display";

// Clamp friend avatars to the warm cream/terracotta palette (DB colours can be
// off-brand teal/blue). Deterministic per person — matches the Dashboard.
const WARM_AVATARS = ["#7A3E32", "#8C5A3C", "#9A4A2A", "#A6674A", "#8A6A32", "#B04A34", "#6B4A3A", "#B5794A"];
function warmAvatar(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return WARM_AVATARS[h % WARM_AVATARS.length];
}

export default function Friends() {
  const { user } = useAuth();
  const { toast } = useToast();
  const userCurrency = user?.defaultCurrency;
  const [addFriendOpen, setAddFriendOpen] = useState(false);
  const [settledExpanded, setSettledExpanded] = useState(false);
  const [inviteLinkCopied, setInviteLinkCopied] = useState(false);
  const [addExpenseOpen, setAddExpenseOpen] = useState(false);
  const [settleUpOpen, setSettleUpOpen] = useState(false);
  const [settleUpFriend, setSettleUpFriend] = useState<{ id: string; name: string; amount: number } | null>(null);
  const [friendEmail, setFriendEmail] = useState("");
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  // Day the money was spent (the posted time is stamped by the server).
  const [spentOn, setSpentOn] = useState(todayYmd());
  const [paidById, setPaidById] = useState("");
  const [splitWithId, setSplitWithId] = useState("");
  const [splitType, setSplitType] = useState<"equal" | "they_pay" | "you_pay">("equal");
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState<"monthly" | "weekly">("monthly");

  // Bridge from Personal Finance ("Split this"): if a draft was stashed, pre-fill
  // the Add Expense form and open it. One-shot — cleared immediately on read.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("spliiit_split_draft");
      if (!raw) return;
      sessionStorage.removeItem("spliiit_split_draft");
      const draft = JSON.parse(raw);
      if (draft && typeof draft.amount === "number") {
        setDescription(typeof draft.description === "string" ? draft.description : "");
        setAmount(String(draft.amount));
        setAddExpenseOpen(true);
      }
    } catch {}
  }, []);
  // Starts on your home currency; picking it converts nothing.
  const homeCurrency = (user?.defaultCurrency || "CAD").toUpperCase();
  const [currency, setCurrency] = useState(homeCurrency);

  const { data: friendsList = [], isLoading } = useQuery<SafeUser[]>({
    queryKey: ["/api/friends"],
  });

  const { data: directExpenses = [] } = useQuery<Expense[]>({
    queryKey: ["/api/friends/expenses"],
  });

  // Exchange rates (premium)
  const { data: ratesData } = useQuery<{ rates: Record<string, number> }>({
    queryKey: ["/api/exchange-rates"],
    enabled: hasFullAccess(user),
    staleTime: 6 * 60 * 60 * 1000,
  });
  const fxRates = ratesData?.rates ?? {};
  // What it will be recorded as, in your home currency (rates are per 1 CAD).
  const cadPreview = (() => {
    if (currency === homeCurrency || !amount) return null;
    const rate = fxRates[currency], home = fxRates[homeCurrency];
    if (!rate || !home) return null;
    const v = (parseFloat(amount) / rate) * home;
    return isNaN(v) ? null : v.toFixed(2);
  })();

  const addFriendMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/friends", {
        email: friendEmail.trim().toLowerCase(),
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/friends"] });
      setFriendEmail("");
      setAddFriendOpen(false);
      toast({ title: "Friend added" });
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
      toast({ title: "Error", description: msg, variant: "destructive" });
    },
  });

  const addExpenseMutation = useMutation({
    mutationFn: async () => {
      let actualPaidById = paidById;
      let splitAmongIds: string[];

      if (splitType === "equal") {
        splitAmongIds = [paidById, splitWithId].filter((v, i, a) => a.indexOf(v) === i);
      } else if (splitType === "they_pay") {
        splitAmongIds = [splitWithId];
      } else {
        actualPaidById = splitWithId;
        splitAmongIds = [paidById];
      }

      const formData = new FormData();
      formData.append("description", description.trim());
      formData.append("amount", String(parseFloat(amount)));
      formData.append("paidById", actualPaidById);
      formData.append("splitAmongIds", JSON.stringify(splitAmongIds));
      formData.append("date", spentOn);
      formData.append("currency", currency);
      if (receiptFile) {
        formData.append("receipt", receiptFile);
      }

      const res = await apiFormRequest("POST", "/api/friends/expenses", formData);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/friends/expenses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/expenses"] });
      track("expense_created", {
        context: "friend",
        split_type: splitType,
        amount: parseFloat(amount),
        currency,
        has_receipt: !!receiptFile,
      });
      resetExpenseForm();
      setAddExpenseOpen(false);
      toast({ title: "Expense added" });
      maybeAskForReview("expense_added");
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
      toast({ title: "Error", description: msg, variant: "destructive" });
    },
  });

  const settleUpMutation = useMutation({
    mutationFn: async () => {
      if (!settleUpFriend) throw new Error("No friend selected");
      const res = await apiRequest("POST", "/api/settle-up", {
        friendId: settleUpFriend.id,
        amount: Math.abs(settleUpFriend.amount),
        // amount > 0 means the friend owes you → they're the one paying it
        // back. The server uses this to record the settlement in the
        // correct direction (otherwise it doubles the balance).
        friendIsPayer: settleUpFriend.amount > 0,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/friends/expenses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/expenses"] });
      track("expense_settled", {
        context: "friend",
        amount: settleUpFriend ? Math.abs(settleUpFriend.amount) : null,
      });
      setSettleUpOpen(false);
      setSettleUpFriend(null);
      toast({ title: "Settled up", description: "Payment recorded" });
      maybeAskForReview("settle_up");
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const addRecurringMutation = useMutation({
    mutationFn: async () => {
      let actualPaidById = paidById;
      let splitAmongIds: string[];

      if (splitType === "equal") {
        splitAmongIds = [paidById, splitWithId].filter((v, i, a) => a.indexOf(v) === i);
      } else if (splitType === "they_pay") {
        splitAmongIds = [splitWithId];
      } else {
        actualPaidById = splitWithId;
        splitAmongIds = [paidById];
      }

      const res = await apiRequest("POST", "/api/recurring", {
        description: description.trim(),
        amount: parseFloat(amount),
        paidById: actualPaidById,
        splitAmongIds,
        groupId: null,
        frequency: recurringFrequency,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/friends/expenses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/expenses"] });
      queryClient.invalidateQueries({ queryKey: ["/api/recurring"] });
      resetExpenseForm();
      setAddExpenseOpen(false);
      toast({ title: "Recurring expense created", description: `First expense added. Repeats ${recurringFrequency}.` });
    },
    onError: (err: Error) => {
      let msg = err.message;
      try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
      toast({ title: "Error", description: msg, variant: "destructive" });
    },
  });

  const resetExpenseForm = () => {
    setDescription("");
    setAmount("");
    setSpentOn(todayYmd());
    setPaidById("");
    setSplitWithId("");
    setSplitType("equal");
    setReceiptFile(null);
    setIsRecurring(false);
    setRecurringFrequency("monthly");
    setCurrency(homeCurrency);
  };

  // Calculate balances across all direct (non-group) expenses
  const settlements = calculatePairwiseBalances(directExpenses);

  // Per-friend balance. displayBalance() snaps sub-$0.05 rounding residuals
  // to 0 so the friends list shows "settled" instead of phantom-cent values.
  const getFriendBalance = (friendId: string) => {
    const relevantExpenses = directExpenses.filter(
      (e) =>
        (e.paidById === user?.id && e.splitAmongIds.includes(friendId)) ||
        (e.paidById === friendId && e.splitAmongIds.includes(user?.id || ""))
    );
    const friendBalances = calculateGroupBalances(relevantExpenses);
    const myBalance = friendBalances.find((b) => b.personId === user?.id);
    return displayBalance(myBalance?.amount || 0);
  };

  const getPersonName = (id: string) => {
    if (id === user?.id) return "You";
    return friendsList.find((f) => f.id === id)?.name || "Someone";
  };

  const sortedExpenses = [...directExpenses].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  const handleSettleUp = (friend: SafeUser) => {
    const balance = getFriendBalance(friend.id);
    if (balance === 0) return;
    setSettleUpFriend({ id: friend.id, name: friend.name, amount: balance });
    setSettleUpOpen(true);
  };

  // Group friends by balance direction. balance > 0 = they owe you (money in),
  // balance < 0 = you owe them (money out), 0 = settled / nothing owed.
  const withBalance = friendsList.map((f) => ({ f, balance: getFriendBalance(f.id) }));
  const youOwe = withBalance.filter((x) => x.balance < 0).sort((a, b) => a.balance - b.balance);
  const owesYou = withBalance.filter((x) => x.balance > 0).sort((a, b) => b.balance - a.balance);
  const settledFriends = withBalance.filter((x) => x.balance === 0);

  const renderRow = (f: SafeUser, balance: number) => (
    <Link key={f.id} href={`/friends/${f.id}`}>
      <Card className="p-[18px] rounded-[26px] flex items-center gap-4 hover-elevate cursor-pointer" data-testid={`friend-card-${f.id}`}>
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-semibold shrink-0"
          style={{ backgroundColor: warmAvatar(f.id) }}
        >
          {f.name[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[17px] font-semibold tracking-tight truncate">{f.name}</p>
        </div>
        {balance !== 0 ? (
          <span className={`font-mono font-semibold shrink-0 whitespace-nowrap ${balance > 0 ? AMOUNT_IN_CLASS : AMOUNT_OUT_CLASS}`}>
            {formatMoney(Math.abs(balance), userCurrency)}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground shrink-0 font-mono">settled</span>
        )}
        <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
      </Card>
    </Link>
  );

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-5xl tracking-tight leading-none">Friends</h1>
          <p className="text-sm text-muted-foreground mt-1.5 font-mono">
            {friendsList.length} {friendsList.length === 1 ? "person" : "people"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Add-expense dialog kept mounted (opens from the Personal Finance
              "Split this" bridge); the visible button is intentionally removed —
              expenses are added from inside a friend or group. */}
          {friendsList.length > 0 && (
            <Dialog open={addExpenseOpen} onOpenChange={setAddExpenseOpen}>
              <DialogContent className="max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Split with a Friend</DialogTitle>
                </DialogHeader>
                <form
                  className="space-y-4 pt-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const blocker =
                      !description.trim() ? "Add a description."
                      : (!amount || (parseFloat(amount) || 0) <= 0) ? "Enter an amount greater than 0."
                      : !paidById ? "Choose who paid."
                      : !splitWithId ? "Choose who to split with."
                      : null;
                    if (blocker) {
                      toast({ title: "Can't add this yet", description: blocker, variant: "destructive" });
                      return;
                    }
                    if (isRecurring) {
                      addRecurringMutation.mutate();
                    } else {
                      addExpenseMutation.mutate();
                    }
                  }}
                >
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input
                      placeholder="e.g. Coffee, Uber ride"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      data-testid="input-direct-desc"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Amount</Label>
                      <CurrencySelector
                        value={currency}
                        onChange={setCurrency}
                        isPremium={hasFullAccess(user)}
                      />
                    </div>
                    <Input
                      type="number"
                      step="0.01"
                      min="0.01"
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      data-testid="input-direct-amount"
                    />
                    {cadPreview && (
                      <p className="text-xs text-muted-foreground font-mono">
                        ≈ {homeCurrency === "CAD" ? "CA$" : currencySymbol(homeCurrency)}{cadPreview} will be recorded
                      </p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="direct-spent-on">Spent on</Label>
                    <Input
                      id="direct-spent-on"
                      type="date"
                      value={spentOn}
                      max={todayYmd()}
                      onChange={(e) => setSpentOn(e.target.value || todayYmd())}
                      data-testid="input-direct-spent-on"
                    />
                    <p className="text-xs text-muted-foreground">Posted today. You both see both dates.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Paid by</Label>
                    <Select value={paidById} onValueChange={setPaidById}>
                      <SelectTrigger data-testid="select-direct-paid-by">
                        <SelectValue placeholder="Who paid?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={user?.id || "me"}>
                          {user?.name} (You)
                        </SelectItem>
                        {friendsList.map((f) => (
                          <SelectItem key={f.id} value={f.id}>
                            {f.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Split with</Label>
                    <Select value={splitWithId} onValueChange={setSplitWithId}>
                      <SelectTrigger data-testid="select-direct-split-with">
                        <SelectValue placeholder="Which friend?" />
                      </SelectTrigger>
                      <SelectContent>
                        {paidById !== user?.id && user?.id && (
                          <SelectItem value={user.id}>
                            {user.name} (You)
                          </SelectItem>
                        )}
                        {friendsList
                          .filter((f) => f.id !== paidById)
                          .map((f) => (
                            <SelectItem key={f.id} value={f.id}>
                              {f.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {/* Split type toggle */}
                  <div className="space-y-2">
                    <Label>How to split</Label>
                    <div className="grid grid-cols-3 gap-1.5">
                      {(["equal", "they_pay", "you_pay"] as const).map((type) => {
                        const labels = {
                          equal: "Split equally",
                          they_pay: "They pay you",
                          you_pay: "You pay them",
                        };
                        return (
                          <button
                            key={type}
                            type="button"
                            className={`px-2 py-2.5 rounded-lg border text-xs font-medium transition-colors ${
                              splitType === type
                                ? "border-primary bg-primary/10 text-primary"
                                : "border-border text-muted-foreground hover:bg-muted/50"
                            }`}
                            onClick={() => setSplitType(type)}
                            data-testid={`split-type-${type}`}
                          >
                            {labels[type]}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {amount && paidById && splitWithId && (
                    <div className="rounded-lg bg-muted/50 p-3 text-center">
                      {splitType === "equal" ? (
                        <p className="text-sm text-muted-foreground">
                          Split equally — <span className="font-medium text-foreground">${(parseFloat(amount) / 2).toFixed(2)}</span> each
                        </p>
                      ) : splitType === "they_pay" ? (
                        <p className="text-sm text-muted-foreground">
                          {paidById === user?.id
                            ? <>{friendsList.find(f => f.id === splitWithId)?.name || "They"} pays you <span className="font-semibold text-foreground">${parseFloat(amount).toFixed(2)}</span></>
                            : <>You pay {friendsList.find(f => f.id === paidById)?.name || "them"} <span className="font-semibold text-destructive">${parseFloat(amount).toFixed(2)}</span></>
                          }
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          {paidById === user?.id
                            ? <>You pay {friendsList.find(f => f.id === splitWithId)?.name || "them"} <span className="font-semibold text-destructive">${parseFloat(amount).toFixed(2)}</span></>
                            : <>{friendsList.find(f => f.id === paidById)?.name || "They"} pays you <span className="font-semibold text-foreground">${parseFloat(amount).toFixed(2)}</span></>
                          }
                        </p>
                      )}
                    </div>
                  )}
                  {/* Repeat toggle (Premium) — hidden entirely in the Android
                      TWA when the user isn't premium (Google Play policy: no
                      premium teaser UI). Web/iOS render normally. Premium
                      users (paid via web Stripe) still see + use the toggle. */}
                  {hasFullAccess(user) && (
                  <div className="rounded-lg border border-border p-3 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Repeat className="w-4 h-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium leading-tight">Repeat this expense</p>
                          <p className="text-xs text-muted-foreground">Auto-creates on schedule</p>
                        </div>
                      </div>
                      <Switch checked={isRecurring} onCheckedChange={setIsRecurring} />
                    </div>
                    {isRecurring && hasFullAccess(user) && (
                      <div className="grid grid-cols-2 gap-1.5 pt-1">
                        {(["monthly", "weekly"] as const).map((freq) => (
                          <button
                            key={freq}
                            type="button"
                            className={`py-2 rounded-lg border text-xs font-medium transition-colors ${
                              recurringFrequency === freq
                                ? "border-primary bg-primary/10 text-primary"
                                : "border-border text-muted-foreground hover:bg-muted/50"
                            }`}
                            onClick={() => setRecurringFrequency(freq)}
                          >
                            {freq === "monthly" ? "Monthly" : "Weekly"}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  )}

                  {/* Receipt (optional) — plain photo attach for THIS manual
                      expense. The AI scan lives BELOW the form (it fills in
                      everything itself, so it's an alternative to manual entry,
                      not a field within it). */}
                  {!isRecurring && (
                  <div className="space-y-2">
                    <Label>Receipt (optional)</Label>
                    {receiptFile ? (
                      <div className="flex items-center gap-2 rounded-lg border border-border p-2.5">
                        <Camera className="w-4 h-4 text-primary shrink-0" />
                        <span className="text-sm truncate flex-1">{receiptFile.name}</span>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground"
                          onClick={() => setReceiptFile(null)}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex items-center gap-2 rounded-lg border border-dashed border-muted-foreground/30 p-3 cursor-pointer hover:bg-muted/50 transition-colors">
                        <Camera className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">Attach receipt photo</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) setReceiptFile(file);
                          }}
                          data-testid="input-direct-receipt"
                        />
                      </label>
                    )}
                    <p className="text-xs text-muted-foreground">Photo will be sent via email to everyone in the split. Not stored.</p>
                  </div>
                  )}
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={addExpenseMutation.isPending || addRecurringMutation.isPending}
                    data-testid="submit-direct-expense"
                  >
                    {(addExpenseMutation.isPending || addRecurringMutation.isPending)
                      ? "Adding..."
                      : isRecurring && hasFullAccess(user)
                        ? `Set Up Recurring (${recurringFrequency})`
                        : "Add Expense"}
                  </Button>

                  {/* ── or — AI-scan the whole receipt instead of the manual form
                      above. AI extracts the merchant, total and date so it's an
                      alternative to the whole form, not a field within it. */}
                  {!isRecurring && (
                    <div className="space-y-3 pt-1">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 h-px bg-border" />
                        <span className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">or</span>
                        <div className="flex-1 h-px bg-border" />
                      </div>
                      <ScanReceiptButton
                        isPremium={hasFullAccess(user)}
                        onResult={async (data, file, scanId) => {
                          // AUTO-CREATE the expense — don't just pre-fill the
                          // form (that confused users into thinking nothing
                          // happened). The review sheet already let them edit
                          // merchant/total before confirming, so we have
                          // everything we need.
                          if (!user) return;
                          if (!splitWithId) {
                            // Edge case: no friend picked yet. Fall back to
                            // pre-fill so the user can complete the form.
                            const scannedDay = receiptDateToYmd(data.date);
                            if (data.merchant && !description.trim()) {
                              setDescription(data.date && !scannedDay ? `${data.merchant} — ${data.date}` : data.merchant);
                            }
                            if (scannedDay) setSpentOn(scannedDay);
                            if (data.total != null && !amount) setAmount(String(data.total));
                            setReceiptFile(file);
                            toast({ title: "Receipt scanned", description: "Pick a friend and tap Add Expense to save." });
                            return;
                          }
                          // Honor the user's current splitType selection.
                          let actualPaidById = paidById || user.id;
                          let splitAmongIds: string[];
                          if (splitType === "equal") {
                            splitAmongIds = [actualPaidById, splitWithId].filter((v, i, a) => a.indexOf(v) === i);
                          } else if (splitType === "they_pay") {
                            splitAmongIds = [splitWithId];
                          } else {
                            actualPaidById = splitWithId;
                            splitAmongIds = [user.id];
                          }
                          try {
                            const fd = new FormData();
                            // The receipt's date becomes the spent date (not part of the name).
                            const receiptYmd = receiptDateToYmd(data.date);
                            const desc = data.merchant
                              ? (data.date && !receiptYmd ? `${data.merchant} — ${data.date}` : data.merchant)
                              : "Receipt";
                            fd.append("description", desc);
                            fd.append("amount", String(data.total ?? 0));
                            fd.append("paidById", actualPaidById);
                            fd.append("splitAmongIds", JSON.stringify(splitAmongIds));
                            fd.append("date", receiptYmd ?? todayYmd());
                            if (scanId) fd.append("aiScanId", scanId);
                            fd.append("receipt", file);
                            await apiFormRequest("POST", "/api/friends/expenses", fd);
                            queryClient.invalidateQueries({ queryKey: ["/api/friends/expenses"] });
                            queryClient.invalidateQueries({ queryKey: ["/api/expenses"] });
                            queryClient.invalidateQueries({ queryKey: ["/api/scan-receipt/quota"] });
                            setAddExpenseOpen(false);
                            resetExpenseForm();
                            toast({ title: "Expense added" });
                          } catch (err: any) {
                            let msg = err.message;
                            try { msg = JSON.parse(msg.split(": ").slice(1).join(": ")).error; } catch {}
                            toast({ title: "Couldn't create expense", description: msg, variant: "destructive" });
                          }
                        }}
                      />
                    </div>
                  )}
                </form>
              </DialogContent>
            </Dialog>
          )}
          <Dialog open={addFriendOpen} onOpenChange={setAddFriendOpen}>
            <DialogTrigger asChild>
              <Button size="icon" variant="outline" className="rounded-full w-11 h-11 shrink-0" data-testid="add-friend-btn" aria-label="Add friend">
                <UserPlus className="w-5 h-5" />
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add a Friend</DialogTitle>
              </DialogHeader>
              <form
                className="space-y-4 pt-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (friendEmail.trim()) addFriendMutation.mutate();
                }}
              >
                <div className="space-y-2">
                  <Label>Friend's Email</Label>
                  <Input
                    type="email"
                    placeholder="friend@example.com"
                    value={friendEmail}
                    onChange={(e) => setFriendEmail(e.target.value)}
                    data-testid="input-friend-email"
                  />
                  <p className="text-xs text-muted-foreground">
                    They need a Spliiit account to connect. Not on Spliiit yet? Share your invite:
                  </p>
                  <ShareChannels
                    url={user?.referralCode ? `https://spliiit.ca?ref=${user.referralCode}` : "https://spliiit.ca"}
                    text={`Hey, ${user?.name || "a friend"} here. I use Spliiit to split expenses with friends. Join me:`}
                    emailSubject="Join me on Spliiit"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={!friendEmail.trim() || addFriendMutation.isPending}
                  data-testid="submit-add-friend"
                >
                  {addFriendMutation.isPending ? "Adding..." : "Add Friend"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Settle Up Dialog */}
      <Dialog open={settleUpOpen} onOpenChange={(open) => { setSettleUpOpen(open); if (!open) setSettleUpFriend(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Settle Up</DialogTitle>
          </DialogHeader>
          {settleUpFriend && (
            <div className="space-y-4 pt-2">
              <div className="rounded-lg bg-muted/50 p-4 text-center space-y-2">
                <p className="text-sm text-muted-foreground">
                  {settleUpFriend.amount < 0 ? (
                    <>You pay <span className="font-semibold text-foreground">{settleUpFriend.name}</span></>
                  ) : (
                    <><span className="font-semibold text-foreground">{settleUpFriend.name}</span> pays you</>
                  )}
                </p>
                <p className="text-2xl font-bold text-primary">
                  {formatMoney(Math.abs(settleUpFriend.amount), userCurrency)}
                </p>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                This will record a payment that zeros out your balance with {settleUpFriend.name}.
              </p>
              <Button
                className="w-full"
                onClick={() => settleUpMutation.mutate()}
                disabled={settleUpMutation.isPending}
                data-testid="confirm-settle-up"
              >
                <CheckCircle2 className="w-4 h-4 mr-1.5" />
                {settleUpMutation.isPending ? "Settling..." : "Confirm Settlement"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Hairline separates the page header from the sections below. */}
      {friendsList.length > 0 && <div className="h-px bg-border" />}

      {/* Friends grouped by balance — scan who owes whom at a glance. Settle by
          tapping a person (settle-up lives in their detail). */}
      {friendsList.length > 0 ? (
        <div className="space-y-8">
          {youOwe.length > 0 && (
            <div>
              <h2 className="font-serif text-3xl tracking-tight leading-none mb-4">You owe</h2>
              <div className="flex flex-col gap-5">
                {youOwe.map(({ f, balance }) => renderRow(f, balance))}
              </div>
            </div>
          )}

          {owesYou.length > 0 && (
            <div>
              <h2 className="font-serif text-3xl tracking-tight leading-none mb-4">Owes you</h2>
              <div className="flex flex-col gap-5">
                {owesYou.map(({ f, balance }) => renderRow(f, balance))}
              </div>
            </div>
          )}

          {settledFriends.length > 0 && (
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setSettledExpanded((v) => !v)}
                className="w-full flex items-center gap-3.5 rounded-[24px] border border-dashed border-border p-4 text-left hover-elevate"
                data-testid="settled-toggle"
              >
                <div className="flex -space-x-2.5 shrink-0">
                  {settledFriends.slice(0, 3).map(({ f }) => (
                    <div
                      key={f.id}
                      className="w-8 h-8 rounded-full border-2 border-background flex items-center justify-center text-white text-xs font-semibold"
                      style={{ backgroundColor: warmAvatar(f.id) }}
                    >
                      {f.name[0]?.toUpperCase()}
                    </div>
                  ))}
                </div>
                <span className="flex-1 text-sm text-muted-foreground min-w-0 truncate">
                  <span className="font-semibold text-foreground">{settledFriends.length} settled</span>
                  {settledFriends.length <= 3 && ` · ${settledFriends.map(({ f }) => f.name.split(" ")[0]).join(", ")}`}
                </span>
                <ChevronRight className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${settledExpanded ? "rotate-90" : ""}`} />
              </button>
              {settledExpanded && (
                <div className="flex flex-col gap-5">
                  {settledFriends.map(({ f, balance }) => renderRow(f, balance))}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        !isLoading && (
          <Card className="p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Users2 className="w-6 h-6 text-primary" />
            </div>
            <h3 className="text-base font-semibold mb-1">No friends yet</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Add a friend by their email to start splitting expenses 1-on-1.
            </p>
          </Card>
        )
      )}

    </div>
  );
}
